import { LinkIcon } from "@heroicons/react/24/outline";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ThreadEntry } from "../../../api/threads/liveMessages";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { formatThreadEntryRef } from "../../../shared/references/threadReferences";
import AgentAvatar from "../../../ui/avatars/AgentAvatar";
import PersonAvatar from "../../../ui/avatars/PersonAvatar";
import IconButton from "../../../ui/buttons/IconButton";
import ForkIcon from "../../../ui/icons/ForkIcon";
import { useCopyToClipboard } from "../../../ui/content/useCopyToClipboard";
import { classNames } from "../../../ui/styles/classNames";
import { useThreadContext } from "../page/threadPageContext";
import { ENTRY_BODY_ATTRIBUTE } from "../../memories/keep/selectionPassages";
import { entryAnchor } from "./entryAnchors";
import { otherPersonsAgent, type AgentTurn } from "./logBlocks";

export type EntryLayout = "card" | "line";

interface EntryFrameProps {
  entry: ThreadEntry;
  /** Who wrote it, as shown. */
  authorLabel: string;
  layout: EntryLayout;
  /** An agent's or a person's words: drawn as a message. */
  tone?: "person" | "agent" | "system";
  /** The agent turn that wrote it: drawn with the agent's avatar, badged with the asker's if not the viewer's. */
  agentTurn?: AgentTurn | null;
  children: ReactNode;
}

/**
 * One entry of the log: its author, time and number, and what anyone can do with it: copy
 * its exact reference `<thread>#<n>` (FR-THREAD-8) and fork the Thread there (FR-THREAD-2).
 * A card's content is marked so selected text can be kept as a memory (memory.md MEM-2).
 * The entry's own Thread is named, so an inherited entry of a fork cites its parent.
 * A person's card has their avatar beside it, the viewer's own on the right (F14); an agent's
 * answer has the agent's, so who answered is always shown (F70).
 */
export default function EntryFrame({ entry, authorLabel, layout, tone = "system", agentTurn = null, children }: EntryFrameProps) {
  const { highlightedEntry, currentUserId, nameOf } = useThreadContext();
  const highlighted = highlightedEntry === entry.number;

  if (layout === "line") {
    return (
      <li id={entryAnchor(entry.number)} className={classNames("group flex items-center gap-2 px-2 py-1 text-xs", highlighted && "app-warning-highlight")}>
        <div className="app-text-muted min-w-0 flex-1">{children}</div>
        <EntryTools entry={entry} />
      </li>
    );
  }
  const cardClass = classNames(
    "group app-card space-y-2 p-3",
    tone === "agent" && "border-l-4 border-l-[var(--app-accent)]",
    tone === "system" && "app-card-muted",
    highlighted && "app-warning-highlight",
  );
  const card = (
    <>
      <div className="flex items-center gap-2">
        <span className="app-title min-w-0 flex-1 truncate text-sm font-semibold">{authorLabel}</span>
        <EntryTools entry={entry} />
      </div>
      {/* The drawn text a person may select and keep as a memory. */}
      <div {...{ [ENTRY_BODY_ATTRIBUTE]: entry.number }} className="space-y-2">{children}</div>
    </>
  );
  if (agentTurn !== null) {
    const owner = otherPersonsAgent(agentTurn, currentUserId);
    return (
      <li id={entryAnchor(entry.number)} className="flex items-start gap-2 pr-10">
        <AgentAvatar owner={owner === null ? null : { userId: owner, name: nameOf(owner) }} className="mt-1" />
        <div className={classNames(cardClass, "min-w-0 flex-1")}>{card}</div>
      </li>
    );
  }
  const person = tone === "person" && entry.author.kind === "user" ? entry.author.user_id : null;
  if (person === null) {
    return <li id={entryAnchor(entry.number)} className={cardClass}>{card}</li>;
  }
  const own = person === currentUserId;
  return (
    <li id={entryAnchor(entry.number)} className={classNames("flex items-start gap-2", own ? "flex-row-reverse pl-10" : "pr-10")}>
      <PersonAvatar userId={person} name={nameOf(person)} className="mt-1" />
      <div className={classNames(cardClass, "min-w-0 flex-1")}>{card}</div>
    </li>
  );
}

/** An entry's copy and fork buttons (shown on hover or focus), its time and number. */
export function EntryTools({ entry }: { entry: ThreadEntry }) {
  const { t, i18n } = useTranslation();
  const { onForkAt } = useThreadContext();
  const { status: copyStatus, copy } = useCopyToClipboard();
  const copyOutcome = copyStatus === "idle" ? null : t(`common.copyReference.${copyStatus}`);
  const reference = formatThreadEntryRef({ threadId: entry.thread_id, number: entry.number });

  const actions = (
    <span className="flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
      <IconButton label={copyOutcome ?? t("threads.entry.copyReference", { reference })} title={reference} onClick={() => { copy(reference); }} className="h-6 w-6 p-1">
        <LinkIcon className="h-3.5 w-3.5" />
      </IconButton>
      {onForkAt === null ? null : (
        <IconButton label={t("threads.entry.forkHere", { number: entry.number })} onClick={() => { onForkAt(entry.number); }} className="h-6 w-6 p-1">
          <ForkIcon className="h-3.5 w-3.5" />
        </IconButton>
      )}
    </span>
  );
  const meta = (
    <span className="app-text-muted flex shrink-0 items-center gap-1.5 text-[11px]">
      <time dateTime={entry.at}>{formatDateTime(entry.at, i18n.resolvedLanguage)}</time>
      <span className="font-mono" title={reference}>#{entry.number}</span>
      <span role="status">{copyOutcome}</span>
    </span>
  );
  return (
    <>
      {actions}
      {meta}
    </>
  );
}
